import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo'
import SearchIcon from '@mui/icons-material/Search'
import {
    Backdrop,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import React, { useEffect, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import { useSKUFulfillmentTypesContext } from '../../../Contexts/skuFulfillmentTypesContext'
import { useSKUOfferingStatusesContext } from '../../../Contexts/skuOfferingStatusesContext'
import { useDistributorSettingsContext } from '../context/DistributorSettingsContext'
import {
    PutProductDistributorFulfillmentType,
    PutProductDistributorOfferingStatus,
    PutProductSettingDistributorOverride,
} from '../../ProductSettings/hooks/useFetchProductSettingsDistributorOverrides'
import { SkuTableRow } from './SkuTableRow'
import {
    enableInMarketDate,
    enableSeasonalForecast,
    SETTINGS_GRID_CELL_PADDING,
    SettingsGridColGroup,
} from '../../../Utilities/settingsGridColumns'

const CUSTOMER_PART_NUMBER_COLUMN = { key: 'customerPartNumber', weight: 7 };

const VirtuosoTableComponents = {
    // eslint-disable-next-line react/display-name
    Scroller: React.forwardRef((props, ref) => (<TableContainer component={Paper} {...props} ref={ref} />)),
    // eslint-disable-next-line react/display-name
    Table: ({ children, ...props }) => (
        <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} size="small">
            <SettingsGridColGroup extraColumns={[CUSTOMER_PART_NUMBER_COLUMN]} />
            {children}
        </Table>
    ),
    // eslint-disable-next-line react/prop-types, no-unused-vars
    TableRow: ({ item, ...props }) => <TableRow {...props} />,
    // eslint-disable-next-line react/display-name
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

export const DistributorSettingsSkuGrid = () => {
    const {
        skuTable,
        updateSkuTable,
        getPossibleSkuFulfillmentTypes,
        customerType,
        isChanged,
        selected,
        searchString,
        setSearchString,
        selectAllLegalRows,
        removeSelectedRows,
        unfilteredSkuTable,
    } = useDistributorSettingsContext();
    const { skuOfferingStatuses } = useSKUOfferingStatusesContext();
    const skuOfferingStatusesForUI = [{
        id: 0, description: "Ignore", skuFulfillmentOverride: null
    }].concat(skuOfferingStatuses);
    const { skuFulfillmentTypes } = useSKUFulfillmentTypesContext();
    const skuFulfillmentTypesForUI = [{ id: 0, description: "Ignore" }].concat(skuFulfillmentTypes);
    const [palletQuantity, setPalletQuantity] = useState("");
    const [palletDivisor, setPalletDivisor] = useState("");
    const [unitWeight, setUnitWeight] = useState("");
    const [offeringStatus, setOfferingStatus] = useState(0);
    const [skuFulfillmentType, setSkuFulfillmentType] = useState(0);
    const [isCopySelectedEnabled, setIsCopySelectedEnabled] = useState(false);
    const [isCopying, setIsCopying] = useState(false);

    useEffect(() => {
        setIsCopySelectedEnabled(isCopyToSelectedEnabled());
    }, [selected, palletQuantity, palletDivisor, unitWeight, offeringStatus, skuFulfillmentType]);

    const buildCombinedRows = () => {
        let selectedRows = unfilteredSkuTable.filter(row => selected.has(row.id));
        const updateObject = {
            ...(palletQuantity !== "" && { palletQuantity }),
            ...(palletDivisor !== "" && { palletDivisor }),
            ...(unitWeight !== "" && { unitWeight }),
            ...(offeringStatus !== 0 && {
                skuOfferingStatus: skuOfferingStatuses.find(status => status.id === offeringStatus),
            }),
        };

        let combinedRows = selectedRows.map(row => {
            return { row: row, updated: { ...row, ...updateObject } };
        })

        combinedRows = combinedRows.map(combinedRow => {
            let fulfillmentType = getPossibleSkuFulfillmentTypes(customerType, combinedRow.updated.skuOfferingStatus?.id)?.skuFulfillmentTypes.find(type => type.id === skuFulfillmentType) ? skuFulfillmentType : combinedRow.row.skuFulfillmentType;
            let possibleSkuFulfillmentTypes = getPossibleSkuFulfillmentTypes(customerType, combinedRow.updated.skuOfferingStatus?.id);

            const isRequestedFulfillmentTypeValid = possibleSkuFulfillmentTypes.skuFulfillmentTypes.find(type => type?.id === skuFulfillmentType);
            if (isRequestedFulfillmentTypeValid) {
                fulfillmentType = isRequestedFulfillmentTypeValid;
            } else {
                fulfillmentType = combinedRow.row.skuFulfillmentType
                const isOriginalFulfillmentTypeValid = possibleSkuFulfillmentTypes.skuFulfillmentTypes.find(type => type?.id === fulfillmentType?.id);
                if (!isOriginalFulfillmentTypeValid) {
                    const defaultType = possibleSkuFulfillmentTypes.skuFulfillmentTypes.find(type => type?.isDefault);
                    fulfillmentType = defaultType || possibleSkuFulfillmentTypes.skuFulfillmentTypes[0];
                }
            }

            combinedRow.updated = { ...combinedRow.updated, skuFulfillmentType: fulfillmentType };
            return combinedRow;
        });

        return combinedRows;
    }

    const processUpdates = async (combinedRows) => {
        for (const combinedRow of combinedRows) {
            if (isChanged(combinedRow.updated, combinedRow.row)) {
                const putTarget = { ...combinedRow.updated, id: combinedRow.updated.distributorId };
                await updateProductSettingsSkuOverride(combinedRow.updated, combinedRow.row);
                await PutProductDistributorOfferingStatus(combinedRow.updated.product, putTarget);
                if (combinedRow.updated.skuFulfillmentType) {
                    await PutProductDistributorFulfillmentType(combinedRow.updated.product, putTarget);
                }
            }
        }
    }

    const handleCopyClick = async () => {
        setIsCopying(true);
        try {
            const combinedRows = buildCombinedRows();
            updateSkuTable(combinedRows.map(combined => combined.updated));
            await processUpdates(combinedRows);
        } catch (error) {
            console.error('Error during copy operation:', error);
        } finally {
            setIsCopying(false);
        }
    }

    const isCopyToSelectedEnabled = () => {
        return (!selected?.size > 0 || (palletQuantity === "" && palletDivisor === "" && unitWeight === "" && offeringStatus === 0 && skuFulfillmentType === 0)) ?? true;
    }

    const fixedHeaderContent = () => {
        const legalRows = skuTable.filter(row => row.legal !== false);
        const isAllLegalRowsSelected = selected.size > 0 && selected.size === legalRows.length;
        const isSomeLegalRowsSelected = selected.size > 0 && selected.size < legalRows.length;

        return (<React.Fragment>
            <TableRow sx={{ backgroundColor: 'white' }}>
                <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Stack direction='row' alignItems='center'>
                        <Checkbox
                            color="primary"
                            indeterminate={isSomeLegalRowsSelected}
                            checked={isAllLegalRowsSelected}
                            onChange={() => {
                                if (isAllLegalRowsSelected) {
                                    removeSelectedRows();
                                } else {
                                    selectAllLegalRows();
                                }
                            }}
                        />
                        <Typography variant={headerVariant}>
                            All SKUs
                        </Typography>
                    </Stack>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Typography variant={headerVariant}>Legal</Typography>
                    <div>&nbsp;</div>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Typography variant={headerVariant}>Pallet Quantity</Typography>
                    <div>&nbsp;</div>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Typography variant={headerVariant}>Pallet Divisor</Typography>
                    <div>&nbsp;</div>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Typography variant={headerVariant}>Unit Weight</Typography>
                    <div>&nbsp;</div>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Typography variant={headerVariant}>Offering Status</Typography>
                    <div>&nbsp;</div>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Typography variant={headerVariant}>Fulfillment Method</Typography>
                    <div>&nbsp;</div>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Typography variant={headerVariant}>Customer Part #</Typography>
                    <div>&nbsp;</div>
                </TableCell>
                {enableSeasonalForecast && (
                    <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                        <Typography variant={headerVariant}>Seasonal</Typography>
                        <div>&nbsp;</div>
                    </TableCell>
                )}
                {enableInMarketDate && (
                    <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                        <Typography variant={headerVariant}>In-Market Date</Typography>
                        <div>&nbsp;</div>
                    </TableCell>
                )}
                {enableInMarketDate && enableSeasonalForecast && (
                    <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                        <Typography variant={headerVariant}>End In-Market Date</Typography>
                        <div>&nbsp;</div>
                    </TableCell>
                )}
            </TableRow>
            <TableRow sx={{ backgroundColor: 'white' }}>
                <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <TextField
                            id="search"
                            size="small"
                            placeholder='Search by sku, name, legal status ("legal", "illegal"), offering status, or fulfillment type'
                            InputProps={{
                                startAdornment: (<InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>),
                            }}
                            value={searchString}
                            onChange={(event) => {
                                setSearchString(event.target.value);
                            }}
                            sx={{ flex: 1, minWidth: 0 }}
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            size="small"
                            disabled={isCopySelectedEnabled || isCopying}
                            startIcon={isCopying ? <CircularProgress size={14} color="inherit" /> : <ContentPasteGoIcon />}
                            onClick={handleCopyClick}
                            sx={{ whiteSpace: 'nowrap' }}>
                            {isCopying ? 'Copying...' : 'Copy To Selected'}
                        </Button>
                    </Stack>
                </TableCell>
                <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }} />
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <TextField
                        placeholder="Ignore"
                        size="small"
                        type="number"
                        fullWidth
                        value={palletQuantity}
                        onChange={event => setPalletQuantity(event.target.value)}
                    />
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <TextField
                        placeholder="Ignore"
                        size="small"
                        type="number"
                        fullWidth
                        value={palletDivisor}
                        onChange={event => setPalletDivisor(event.target.value)}
                    />
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <TextField
                        placeholder="Ignore"
                        size="small"
                        type="number"
                        fullWidth
                        value={unitWeight}
                        onChange={event => setUnitWeight(event.target.value)}
                    />
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <TextField
                        id="outlined-select-fulfillment"
                        select
                        value={offeringStatus}
                        size="small"
                        fullWidth
                        onChange={event => setOfferingStatus(event.target.value)}
                    >
                        {skuOfferingStatusesForUI.map(status => <MenuItem key={status.id}
                            value={status.id}>
                            {status.description}
                        </MenuItem>)}
                    </TextField>
                </TableCell>
                <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                    <TextField
                        id="outlined-select-fulfillment"
                        select
                        value={skuFulfillmentType}
                        size="small"
                        fullWidth
                        onChange={event => setSkuFulfillmentType(event.target.value)}
                    >
                        {skuFulfillmentTypesForUI.map(fulfillmentType => <MenuItem key={fulfillmentType.id}
                            value={fulfillmentType.id}>
                            {fulfillmentType.description}
                        </MenuItem>)}
                    </TextField>
                </TableCell>
                <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }} />
                {enableSeasonalForecast && <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }} />}
                {enableInMarketDate && <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }} />}
                {enableInMarketDate && enableSeasonalForecast && <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }} />}
            </TableRow>
        </React.Fragment>);
    }

    const handleUpdateRow = async (updatedRow) => {
        let originalRow = skuTable.find(row => row.id === updatedRow.id);

        if (isChanged(updatedRow, originalRow)) {
            updateSkuTable([updatedRow]);
        }
    }

    const updateProductSettingsSkuOverride = async (updatedRow, originalRow) => {
        if (isChanged(updatedRow, originalRow)) {
            await PutProductSettingDistributorOverride(updatedRow.product, { ...updatedRow, id: updatedRow.distributorId })
        }
    };

    const RowComponent = (_index, row) => {
        return (<SkuTableRow row={row} handleUpdateRow={handleUpdateRow}
            onSaveProductSettingsSkuOverride={updateProductSettingsSkuOverride}
            isChanged={isChanged}
        />)
    }

    return (<Box sx={{ height: "100%", width: "100%" }}>
        <Paper elevation={1} sx={{ height: '100%', width: '100%' }}>
            <TableVirtuoso
                data={skuTable}
                components={VirtuosoTableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={RowComponent}
            />
        </Paper>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isCopying}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress color="inherit" />
                <Typography variant="h6">Copying to selected SKUs...</Typography>
            </Box>
        </Backdrop>
    </Box>);
}

const headerVariant = 'h6'
